import { useRef, useState } from "react";
import { toast } from "sonner";
import { X, Sparkles, Loader2, Copy, Replace, ListPlus, Square } from "lucide-react";
import { Button } from "../ui/button";
import { getSavedRange, setSavedRange, insertHTML, insertBlock, restore } from "./cmd";
import { esc } from "../../lib/pages";
import { getLicense, setLicense, deviceId } from "../../lib/pro";
import { ProDialog, usePro } from "../ProDialog";

const MODES = [["detect", "Detect issues"], ["suggest", "Suggest"], ["rephrase", "Rephrase"], ["reformat", "Reformat"]];
const TONES = ["professional", "formal", "friendly", "concise", "persuasive", "simple"];
const ALLOWED = /^(H2|H3|P|UL|OL|LI|B|STRONG|I|EM|TABLE|TBODY|TR|TD|TH|BR)$/;

const sanitize = (html) => {
  const t = document.createElement("div");
  t.innerHTML = html.replace(/^```(html)?|```$/g, "");
  t.querySelectorAll("*").forEach((el) => {
    if (!ALLOWED.test(el.tagName)) el.replaceWith(...el.childNodes);
    else [...el.attributes].forEach((a) => el.removeAttribute(a.name));
  });
  return t.innerHTML;
};

export const AiPanel = ({ rootRef, onClose }) => {
  const [mode, setMode] = useState("detect");
  const [tone, setTone] = useState("professional");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const [usedSel, setUsedSel] = useState(false);
  const abort = useRef(null);
  const lic = usePro();
  const [proOpen, setProOpen] = useState(false);

  const run = async () => {
    const r = getSavedRange();
    const sel = r && !r.collapsed && rootRef.current.contains(r.commonAncestorContainer) ? r.toString() : "";
    const text = (sel || rootRef.current.innerText).replace(/\u200B/g, "").trim();
    if (!text) return toast("The document is empty");
    if (!getLicense()) return setProOpen(true);
    if (!navigator.onLine) return toast.error("The AI assistant needs an internet connection");
    setUsedSel(!!sel);
    setOut("");
    setBusy(true);
    abort.current = new AbortController();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/assist`, {
        method: "POST", headers: { "Content-Type": "application/json", "X-License": getLicense(), "X-Device": deviceId() }, signal: abort.current.signal,
        body: JSON.stringify({ mode, tone, text: text.slice(0, 30000) }),
      });
      if (res.status === 402) { setLicense(""); localStorage.removeItem("lw.proLicense"); setProOpen(true); throw new Error("PaperLite Pro is required for the AI assistant"); }
      if (!res.ok) throw new Error(`AI request failed (${res.status})`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setOut(mode === "reformat" ? acc : acc.replace(/\*\*|__|^#+\s/gm, ""));
      }
    } catch (x) {
      if (x.name !== "AbortError") toast.error(x.message || "AI request failed");
    }
    setBusy(false);
  };

  const html = mode === "reformat" ? sanitize(out) : out.split(/\n{2,}/).map((p) => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`).join("");
  const apply = (replace) => {
    restore();
    if (!replace && window.getSelection().rangeCount) window.getSelection().collapseToEnd();
    const s = window.getSelection();
    if (replace && !usedSel) {
      const all = document.createRange();
      all.selectNodeContents(rootRef.current);
      s.removeAllRanges();
      s.addRange(all);
    }
    if (s.rangeCount) setSavedRange(s.getRangeAt(0).cloneRange());
    if (!replace) insertBlock(html);
    else insertHTML(mode === "rephrase" && usedSel ? esc(out.trim()) : html);
    toast.success(replace ? "Replaced with AI result" : "AI result inserted");
  };

  return (
    <aside className="w-[340px] shrink-0 border-l bg-card flex flex-col" data-testid="ai-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2 font-semibold text-sm"><Sparkles className="h-4 w-4 text-primary" />AI writing assistant</div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} data-testid="ai-close-btn"><X className="h-4 w-4" /></Button>
      </div>
      <div className="p-4 space-y-3 border-b">
        <div className="grid grid-cols-2 gap-1.5">
          {MODES.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setMode(k)} data-testid={`ai-mode-${k}`}
              className={`h-8 rounded-md border text-xs font-medium transition-colors ${mode === k ? "border-primary bg-primary/10 text-primary" : "hover:bg-accent"}`}>{l}</button>
          ))}
        </div>
        {mode === "rephrase" && (
          <select value={tone} onChange={(e) => setTone(e.target.value)} className="w-full h-8 rounded-md border bg-background px-2 text-sm" data-testid="ai-tone-select">
            {TONES.map((t) => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)} tone</option>)}
          </select>
        )}
        <p className="text-[11px] text-muted-foreground">Works on the selected text, or the whole document if nothing is selected. Only that text is sent to the AI (Claude); your library stays in this browser.</p>
        {busy
          ? <Button size="sm" variant="outline" className="w-full" onClick={() => abort.current?.abort()} data-testid="ai-stop-btn"><Square className="h-3.5 w-3.5 mr-1.5" />Stop</Button>
          : <Button size="sm" className="w-full" onClick={run} data-testid="ai-run-btn"><Sparkles className="h-4 w-4 mr-1.5" />{lic ? "Run" : "Run · Pro feature"}</Button>}
      </div>
      <div className="flex-1 overflow-y-auto p-4" data-testid="ai-output">
        {busy && !out && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Thinking…</div>}
        {out && (mode === "reformat"
          ? <div className="doc-content text-sm" dangerouslySetInnerHTML={{ __html: html }} />
          : <div className="text-sm whitespace-pre-wrap leading-relaxed">{out}</div>)}
      </div>
      {out && !busy && (
        <div className="p-3 border-t flex gap-1.5">
          {(mode === "rephrase" || mode === "reformat") && <Button size="sm" className="flex-1" onClick={() => apply(true)} data-testid="ai-replace-btn"><Replace className="h-3.5 w-3.5 mr-1" />Replace</Button>}
          <Button size="sm" variant="outline" className="flex-1" onClick={() => apply(false)} data-testid="ai-insert-btn"><ListPlus className="h-3.5 w-3.5 mr-1" />Insert</Button>
          <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(out); toast("Copied"); }} data-testid="ai-copy-btn"><Copy className="h-3.5 w-3.5" /></Button>
        </div>
      )}
      <ProDialog open={proOpen} onClose={() => setProOpen(false)} />
    </aside>
  );
};
